<script setup lang="ts">
  import { ref, computed, watch, onBeforeUnmount } from 'vue';
  import type { ZoomProps } from '@/features/FormBuilder';
  import { CANVAS_OUTER_PADDING } from '../../../lib/canvas-constants';

  interface Props extends ZoomProps {
    zoom: number;
    displayWidth: number;
    pageScroll?: boolean;
    contentHeight?: number;
    /** Scale that fits the sheet to the screen; the user zoom multiplies it. */
    fitScale?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    minZoom: 0.5,
    maxZoom: 2,
    zoomStep: 0.1,
    pageScroll: false,
    contentHeight: 0,
    fitScale: 1,
  });

  const emit = defineEmits<{
    'update:zoom': [value: number];
    'update:scrollTop': [value: number];
  }>();

  const clampZoom = (value: number) => Math.min(props.maxZoom, Math.max(props.minZoom, value));

  const userZoom = ref(clampZoom(props.zoom));
  const zoomWrapperRef = ref<HTMLElement | null>(null);

  const isDragging = ref(false);
  const startX = ref(0);
  const startY = ref(0);
  const scrollLeftVal = ref(0);
  const scrollTopVal = ref(0);
  const scrollAnimationFrame = ref<number | null>(null);

  const scale = computed(() => userZoom.value * props.fitScale);
  const scaledWidth = computed(() => props.displayWidth * scale.value);
  const scaledHeight = computed(() => props.contentHeight * scale.value);

  watch(
    [() => props.zoom, () => props.minZoom, () => props.maxZoom],
    ([newZoom]) => {
      const clampedZoom = clampZoom(newZoom);
      if (Math.abs(clampedZoom - userZoom.value) > 0.001) {
        userZoom.value = clampedZoom;
      } else if (Math.abs(clampedZoom - newZoom) > 0.001) {
        emit('update:zoom', clampedZoom);
      }
    },
    { immediate: true }
  );

  watch(userZoom, (newZoom) => {
    if (Math.abs(newZoom - props.zoom) > 0.001) {
      emit('update:zoom', newZoom);
    }
  });

  const animateScrollTo = (targetLeft: number, targetTop: number, duration = 300) => {
    if (!zoomWrapperRef.value) return;

    if (scrollAnimationFrame.value !== null) {
      cancelAnimationFrame(scrollAnimationFrame.value);
      scrollAnimationFrame.value = null;
    }

    const startLeft = zoomWrapperRef.value.scrollLeft;
    const startTop = zoomWrapperRef.value.scrollTop;
    const startTime = performance.now();

    const step = (now: number) => {
      if (!zoomWrapperRef.value) return;

      const progress = Math.min((now - startTime) / duration, 1);
      const easedProgress = 1 - (1 - progress) ** 3;

      zoomWrapperRef.value.scrollLeft = startLeft + (targetLeft - startLeft) * easedProgress;
      zoomWrapperRef.value.scrollTop = startTop + (targetTop - startTop) * easedProgress;

      if (progress < 1) {
        scrollAnimationFrame.value = requestAnimationFrame(step);
      } else {
        scrollAnimationFrame.value = null;
      }
    };

    scrollAnimationFrame.value = requestAnimationFrame(step);
  };

  const adjustScrollForZoom = (e: WheelEvent, oldZoom: number, newZoom: number) => {
    if (!zoomWrapperRef.value) return;

    const rect = zoomWrapperRef.value.getBoundingClientRect();

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const contentX = mouseX + zoomWrapperRef.value.scrollLeft;
    const contentY = mouseY + zoomWrapperRef.value.scrollTop;

    const scaleRatio = newZoom / oldZoom;
    const newScrollLeft = contentX * scaleRatio - mouseX;
    const newScrollTop = contentY * scaleRatio - mouseY;

    animateScrollTo(newScrollLeft, newScrollTop);
  };

  const zoomIn = (e?: WheelEvent) => {
    const oldZoom = userZoom.value;
    userZoom.value = Math.min(
      props.maxZoom,
      Math.round((userZoom.value + props.zoomStep) * 100) / 100
    );
    if (e) {
      requestAnimationFrame(() => adjustScrollForZoom(e, oldZoom, userZoom.value));
    }
  };

  const zoomOut = (e?: WheelEvent) => {
    if (userZoom.value > props.minZoom) {
      const oldZoom = userZoom.value;
      userZoom.value = Math.max(
        props.minZoom,
        Math.round((userZoom.value - props.zoomStep) * 100) / 100
      );
      if (e) {
        requestAnimationFrame(() => adjustScrollForZoom(e, oldZoom, userZoom.value));
      }
    }
  };

  const handleWheel = (e: WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    // In page-scroll mode the wrapper is not the scroller, so there is no scroll to anchor on the pointer.
    const anchor = props.pageScroll ? undefined : e;
    if (e.deltaY < 0) {
      zoomIn(anchor);
    } else {
      zoomOut(anchor);
    }
  };

  const handleMouseDown = (e: MouseEvent) => {
    if (props.pageScroll) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      isDragging.value = true;

      if (!zoomWrapperRef.value) return;

      startX.value = e.pageX - zoomWrapperRef.value.offsetLeft;
      startY.value = e.pageY - zoomWrapperRef.value.offsetTop;
      scrollLeftVal.value = zoomWrapperRef.value.scrollLeft;
      scrollTopVal.value = zoomWrapperRef.value.scrollTop;
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.value || !zoomWrapperRef.value) return;

    e.preventDefault();

    const x = e.pageX - zoomWrapperRef.value.offsetLeft;
    const y = e.pageY - zoomWrapperRef.value.offsetTop;

    const walkX = (x - startX.value) * 2;
    const walkY = (y - startY.value) * 2;

    zoomWrapperRef.value.scrollLeft = scrollLeftVal.value - walkX;
    zoomWrapperRef.value.scrollTop = scrollTopVal.value - walkY;
  };

  const handleMouseUp = () => {
    isDragging.value = false;
  };

  const handleMouseLeave = () => {
    isDragging.value = false;
  };

  const handleScroll = () => {
    if (zoomWrapperRef.value) {
      emit('update:scrollTop', zoomWrapperRef.value.scrollTop);
    }
  };

  onBeforeUnmount(() => {
    if (scrollAnimationFrame.value !== null) {
      cancelAnimationFrame(scrollAnimationFrame.value);
    }
  });
</script>

<template>
  <div
    class="relative box-border flex w-full flex-col"
    :class="pageScroll ? '' : 'flex-1 overflow-hidden'"
  >
    <div
      ref="zoomWrapperRef"
      data-testid="zoom-wrapper"
      class="relative box-border w-full"
      :class="[
        pageScroll
          ? ''
          : 'flex-1 overflow-auto cursor-default active:cursor-grab [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#888] hover:[&::-webkit-scrollbar-thumb]:bg-[#555] [&::-webkit-scrollbar-track]:bg-gray-14',
        isDragging ? 'is-dragging !cursor-grabbing select-none' : '',
      ]"
      :data-dragging="isDragging"
      @scroll="handleScroll"
      @wheel="handleWheel"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseLeave"
    >
      <div
        class="box-border flex w-max min-w-full pt-2"
        :style="{ paddingInline: `${CANVAS_OUTER_PADDING}px` }"
      >
        <div
          data-testid="zoom-area"
          class="mx-auto transition-[width] duration-300 ease-in-out"
          :style="pageScroll ? { width: `${scaledWidth}px`, height: `${scaledHeight}px` } : { width: `${scaledWidth}px` }"
        >
          <div
            data-testid="zoom-scaled"
            class="transform-gpu transition-transform duration-300 ease-in-out will-change-transform"
            :style="{
              transform: `scale(${scale})`,
              transformOrigin: '0 0',
              width: `${props.displayWidth}px`,
            }"
          >
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
